import { Link } from "react-router-dom";
import { ArrowUpRight, FileBadge, ShieldCheck, Landmark, MapPin } from "lucide-react";
import { IMAGES, COMPANY, SERVICE_AREA, CUSTOMERS } from "../data/site";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";

const CREDENTIALS = [
  {
    icon: FileBadge,
    title: "Multi-State Licensed",
    desc: "CA #421831 (Class A & C-12) · AZ #327162 · NV #0086504.",
  },
  {
    icon: ShieldCheck,
    title: "Fully Insured",
    desc: "General liability and workers compensation. Current coverage certificates available on request and customizable to your specifications.",
  },
  {
    icon: Landmark,
    title: "Bank Supported",
    desc: "Full support of our local bank, American Riviera, with an unprecedented work record placing plant facilities.",
  },
  {
    icon: MapPin,
    title: "Regional Reach",
    desc: "Central California base — serving California, Nevada, and Arizona from coast to border. Any project in the region considered.",
  },
];

export default function About() {
  return (
    <>
      <PageHero
        testId="about-hero"
        eyebrow={`${COMPANY.name} // ${COMPANY.years} Years`}
        lines={["The company", "beneath it all."]}
        sub="A utilities contractor for forty-six years — substructure: plowing, trenching, excavating, directional boring, rodding, pulling, paving, conduit and cable placing, fiber placing and pulling, and manhole placement."
        image={IMAGES.excavator}
      />

      <section data-testid="about-story" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
            <div>
              <Reveal>
                <Eyebrow>01 // The Work</Eyebrow>
                <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
                  Dry utilities, done right
                </h2>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-400">
                  <p>
                    Our present markets include all dry utilities, specializing in underground
                    telephone and power substructure placement. Our customers include major
                    companies — {CUSTOMERS.slice(0, 8).join(", ")}, and more.
                  </p>
                  <p>
                    The opportunities for utility construction have been dynamic. Existing
                    substructure needs upgrading, fiber optic is the choice of the future, and
                    the demand for turn-key work exceeds the capabilities of most contractors.
                    LTEC can and will get the job done.
                  </p>
                  <p>
                    We submit proposals on large projects as well as day-to-day time-and-material
                    jobs — with modern computer systems, up-to-date technology, and trained
                    personnel proficient in current industry software.
                  </p>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="clip-frame relative overflow-hidden border border-white/10">
                <img
                  src={IMAGES.excavator}
                  alt="LTEC excavator on a utility jobsite"
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">
                  Field Ops // Central CA
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-credentials" className="border-y border-white/10 bg-granite/50 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>02 // Credentials</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
              Licensed, insured, accountable
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
            {CREDENTIALS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="h-full bg-obsidian p-6 sm:p-8">
                  <c.icon className="h-7 w-7 text-brand-light" />
                  <h3 className="mt-4 font-display text-2xl font-bold uppercase text-white">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-12 border border-white/10 bg-obsidian p-6 sm:p-8">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
                Service Area
              </p>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-service-area">
                {SERVICE_AREA.map((area) => (
                  <li key={area} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <span className="h-1.5 w-1.5 rotate-45 bg-brand-light" />
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-cta" className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
              Put {COMPANY.years} years underground on your next project
            </h2>
            <Link
              to="/request-bid"
              data-testid="about-cta-contact"
              className="mt-8 inline-flex items-center gap-2 bg-brand px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-light"
            >
              Start the Conversation <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
